import {
  type AuthSession,
  type LoginPayload,
  type RegisterPayload,
} from '@repo/api-client';

import { readAccessToken, useSessionStore } from './session.store';

import { api } from '@/lib/api';
import { configureHttpAuth } from '@/lib/http';
import { storage, storageKey } from '@/lib/storage';

/**
 * Session lifecycle — deliberately outside React.
 *
 * Warning: the refresh token is an explicit stored value, never an httpOnly cookie
 * (see ADR 0001) — it is readable from JS, hence `lib/storage`.
 *
 * @see docs/features/auth.md
 */

const REFRESH_TOKEN_KEY = storageKey('refreshToken');

async function persist(session: Pick<AuthSession, 'refreshToken'>) {
  await storage.set(REFRESH_TOKEN_KEY, session.refreshToken);
}

async function forget() {
  await storage.remove(REFRESH_TOKEN_KEY);
}

/*
 * Refresh, de-duplicated: parallel 401s must share one in-flight refresh,
 * or the second call's token rotation would invalidate the first.
 */
let inFlightRefresh: Promise<boolean> | null = null;

/**
 * The one call that must never be renewed by a renewal: itself.
 *
 * Warning: without this, a revoked or expired token deadlocked the startup.
 * The refresh call answered 401, the transport asked the session to refresh,
 * the session handed back the very promise it was in the middle of — which
 * was waiting on that same call. Nothing settled, `status` never left
 * `loading`, and the application stayed on its spinner until reloaded, which
 * changed nothing.
 */
const REFRESH_PATH = '/auth/refresh';

function refreshOnce(): Promise<boolean> {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function performRefresh(): Promise<boolean> {
  const refreshToken = await storage.get(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;

  try {
    const renewed = await api.auth.refresh(refreshToken);
    useSessionStore.getState().renewAccessToken(renewed.accessToken);
    await persist(renewed);
    return true;
  } catch {
    // Revoked, expired, or unreachable: fall back to anonymous rather than loop on 401s.
    await forget();
    useSessionStore.getState().reset();
    return false;
  }
}

/**
 * Wires the transport to the session, once at startup before any render.
 *
 * `lib/http` cannot depend on `features/auth`, so the feature registers itself.
 */
export function connectHttpToSession(): void {
  configureHttpAuth({
    getAccessToken: readAccessToken,
    onUnauthorized: (path) =>
      path === REFRESH_PATH ? Promise.resolve(false) : refreshOnce(),
  });
}

/**
 * Restores a session at startup from the stored token.
 *
 * `status` stays `loading` until this settles, which is what stops a reload
 * from briefly bouncing an already-authenticated user to the login screen.
 */
export async function restoreSession(): Promise<void> {
  const { reset, renewAccessToken, updateUser } = useSessionStore.getState();

  const refreshToken = await storage.get(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    reset();
    return;
  }

  try {
    const renewed = await api.auth.refresh(refreshToken);
    renewAccessToken(renewed.accessToken);
    await persist(renewed);

    const user = await api.users.getMe();
    updateUser(user);
    useSessionStore.setState({ status: 'authenticated' });
  } catch {
    await forget();
    reset();
  }
}

/**
 * Adopts a token pair obtained outside the login form: QR sign-in, or an OAuth return.
 *
 * The single session entry point — persistence, profile load and the
 * `authenticated` transition are identical regardless of how the tokens arrived.
 */
export async function adoptSession(tokens: {
  accessToken: string;
  refreshToken: string;
}): Promise<void> {
  await persist(tokens);

  // The token must be in place BEFORE `/users/me`, which is authenticated.
  useSessionStore.getState().renewAccessToken(tokens.accessToken);
  const user = await api.users.getMe();

  useSessionStore.getState().authenticate(tokens.accessToken, user);
}

/**
 * Re-reads the signed-in profile and puts it back in the store.
 *
 * ⚠️ For the fields a guard reads. The session keeps the profile it was handed
 * at sign-in, so anything the server changes afterwards is invisible until the
 * next reload — and `emailVerified` is changed by following a link, on this
 * device or another. Without this, confirming an address left `RequireAuth`
 * still looking at a stale `false` and bouncing the reader back to the wall
 * they had just cleared.
 *
 * Warning: the session must already be established. It is not a way to open
 * one — `adoptSession` is.
 */
export async function refreshCurrentUser(): Promise<void> {
  const user = await api.users.getMe();
  useSessionStore.getState().updateUser(user);
}

/** Email / password sign-in. */
export async function signIn(payload: LoginPayload): Promise<void> {
  // `POST /auth/login` returns an `AuthUser` without avatar or emailVerified,
  // so the full profile still comes from `adoptSession`.
  const session = await api.auth.login(payload);
  await adoptSession(session);
}

/**
 * Standard signup. The account is usable immediately — email verification
 * happens later and blocks nothing.
 */
export async function signUp(payload: RegisterPayload): Promise<void> {
  const session = await api.auth.register(payload);
  await adoptSession(session);
}

/**
 * Sign-out. A server failure never blocks the local sign-out — the user
 * asked to leave, a dropped network should not hold them back.
 */
export async function signOut(): Promise<void> {
  const refreshToken = await storage.get(REFRESH_TOKEN_KEY);

  try {
    if (refreshToken) await api.auth.logout(refreshToken);
  } catch {
    /* server-side revocation failed: clean up locally anyway */
  } finally {
    await forget();
    useSessionStore.getState().reset();
  }
}
